import { IUser } from '../types/reducers/authReducer';
import { landingRouteFor, DASHBOARD_ROOT } from '../backend/sagas/onboardingRedirect';

export type PermissionsType = {
  general: string[];
  scoped: {
    routes: number[];
    households: number[];
    permissions: string[];
  }
};

export function hasPermission(
  permissionList: string | string[],
  user: IUser | null,
  all = false
): boolean {
  if (!user) {
    return false;
  }

  const required = Array.isArray(permissionList) ? permissionList : [permissionList];

  const generalPermissions = user.permissions.general || [];
  const scopedPermissions = user.permissions.scoped?.permissions || [];

  const generalHas = all
    ? required.every(p => generalPermissions.includes(p))
    : required.some(p => generalPermissions.includes(p));

  if (generalHas) {
    return true;
  }

  const scopedHas = all
    ? required.every(p => scopedPermissions.includes(p))
    : required.some(p => scopedPermissions.includes(p));

  if (scopedHas) {
    return true;
  }

  return false;
}

export function hasScopedPermission(
  scope: 'routes' | 'households',
  entityId: number,
  permissionList: string | string[],
  scopedPermissions: PermissionsType['scoped'],
  all = false
): boolean {
  const required = Array.isArray(permissionList) ? permissionList : [permissionList];

  const includeAllKey = `include_all_${scope}` as keyof typeof scopedPermissions;
  const includesAll = Boolean(scopedPermissions?.[includeAllKey]);
  const hasEntity = includesAll || scopedPermissions?.[scope]?.includes(entityId);

  const hasPermission = scopedPermissions?.permissions?.length
    ? all
      ? required.every(p => scopedPermissions.permissions.includes(p))
      : required.some(p => scopedPermissions.permissions.includes(p))
    : false;

  return hasEntity && hasPermission;
}


export function hasUserPermission(
  user: IUser | null,
  permissionList: string | string[],
  scope?: { type: 'routes' | 'households'; entity_id: number },
  all = false
): boolean {
  if (!user || !user.permissions) return false;

  if (
    hasPermission(
      permissionList,
      user,
      all
    )
  ) {
    return true;
  }

  if (scope) {
    return hasScopedPermission(
      scope.type,
      scope.entity_id,
      permissionList,
      user.permissions.scoped,
      all
    );
  }

  return false;
}

export function canAccessAny(
  user: IUser | null,
  permission: string,
  scopeType?: 'routes' | 'households'
): boolean {
  if (!user || !user.permissions) return false;

  const { general, scoped } = user.permissions;

  if (general.includes(permission)) return true;

  if (!scopeType) return false;

  const includeAll =
    scopeType === 'routes'
      ? scoped.include_all_routes
      : scoped.include_all_households;

  const hasAnyIds = scoped?.[scopeType]?.length > 0;

  // A grant made of CATEGORIES can legitimately reach nobody at this moment —
  // nothing carries the tag yet, or the tag was deleted — and it still deserves
  // the page: the backend opens the list (HouseholdPolicy::viewAny) and returns
  // it empty. Without this the entry vanishes from the sidebar the day the last
  // tagged contact is untagged, stranding whoever is on the page.
  const holdsScopedPermission = Boolean(scoped?.permissions?.includes(permission));

  return Boolean(includeAll || hasAnyIds || (scopeType === 'households' && holdsScopedPermission));
}

// The user's effective role in their current workspace (falls back to the top-level role).
export function getWorkspaceRole(user: IUser | null): string | undefined {
  if (!user) return undefined;
  const workspace = user.workspaces?.find((ws: any) => ws.id === user.currentWorkspace);
  return workspace ? workspace.workspaceRole : user.role;
}

/**
 * Whether this user is in the "may open/claim a Chabad House for an
 * already-registered account" bracket — admin or super_admin.
 *
 * Fix round 1: deliberately reads `user.role` DIRECTLY, not
 * `getWorkspaceRole(user)`. `user.role` is `UserResource`'s `'role' =>
 * $this->role`, which on the backend (`User::role()`) always resolves
 * against the account's MAIN workspace (`wherePivot('main', true)`) —
 * exactly what `ChabadCenterPolicy::connect` checks for
 * `POST /api/centers/connect`. `getWorkspaceRole(user)` answers a DIFFERENT
 * question — "what is this role in the CURRENTLY SELECTED workspace" — which
 * disagreed with the backend both ways: an admin of a secondary workspace
 * would see this offered and then get a bare 403 on submit, and a genuine
 * main-workspace admin with a different workspace currently selected would
 * never see it at all. Kept as one shared helper so the dashboard row that
 * offers this screen (`GettingStartedCard.tsx`) can never disagree with
 * whether the route itself (`AdminOrSuperAdminRoute` on `/chabad-house/add`,
 * see `hocs/adminOrSuperAdminRoute.tsx`) will actually let the click
 * through — both now ask the SAME (main-workspace) question the backend
 * does. `getWorkspaceRole` itself is left untouched: it's used elsewhere for
 * genuinely current-workspace-scoped questions (e.g. `canExportData`), and
 * re-pointing it would be a much wider, unreviewed change.
 */
export function isAdminOrSuperAdmin(user: IUser | null): boolean {
  if (!user) return false;
  return user.role === 'admin' || user.role === 'super_admin';
}

/**
 * THE definition of "volunteering is all this person can do" on the client.
 *
 * Keyed on his WORKSPACE ROLE, not on the shape of his permission list.
 *
 * Fix round 2 (2026-09-08): it was originally the permission shape -- holds
 * `manage_volunteering`, holds no `read_households` -- which was correct until
 * the IMPORTANT 3 fix in the same wave started injecting `manage_volunteering`
 * into `permissions.general` for anyone who holds it through ANY membership
 * (User::fetchPermissionsForWorkspace). A `limited` member with no scoped
 * grants has an otherwise empty `general`, so an anash/admin/volunteer invited
 * into a second workspace with nothing ticked suddenly presented as
 * `["manage_volunteering"]` and was misclassified volunteer-only THERE: he lost
 * the AppBar gear, /settings and /duchot, and landingRouteFor sent him to
 * /volunteering. The role cannot drift that way.
 *
 * This agrees exactly with the server-side test in
 * EnsureWorkspaceHasSubscription (`hasGlobalPermission('manage_volunteering')
 * && ! hasGlobalPermission('read_households')`): `volunteer` is the only role
 * in RoleSeeder holding manage_volunteering without read_households, and
 * hasGlobalPermission() hardcodes `limited` to false, so the two are
 * equivalent for every role. Keep them that way.
 *
 * It lives here rather than in utils/showsVolunteering.ts because it needs
 * getWorkspaceRole above and that file is deliberately dependency-free;
 * putting it here also keeps backend/sagas/onboardingRedirect.ts importing from
 * exactly the module it already did, adding no new import cycle.
 */
export function isVolunteerOnly(user: IUser | null | undefined): boolean {
  return getWorkspaceRole(user ?? null) === "volunteer";
}

// Roles that land on — and are allowed to access — the /dashboard page.
export const DASHBOARD_ROLES = ["admin", "anash", "super_admin"];

// Roles allowed to bulk-export/download data (the Contacts book, the Reports sheet).
//
// Kept separate from DASHBOARD_ROLES even though the members currently match — they answer
// different questions and should be free to diverge. This mirrors DATA_EXPORT_ROLES in the
// backend's app/Support/helpers.php, which is what actually ENFORCES the rule; hiding the
// buttons here is only so secondary users are not shown an action that would 403.
export const DATA_EXPORT_ROLES = ["admin", "anash", "super_admin"];

/** Whether a workspace role string may bulk-export data. */
export function roleCanExportData(workspaceRole?: string | null): boolean {
  return !!workspaceRole && DATA_EXPORT_ROLES.includes(workspaceRole);
}

/** Whether this user may bulk-export data from their current workspace. */
export function canExportData(user: IUser | null): boolean {
  return roleCanExportData(getWorkspaceRole(user));
}

/**
 * Task 12 fix round 1 (2026-09-08): this is the ONE function every real
 * sign-in call site actually calls (LoginPage.tsx, AuthLayout.tsx,
 * App/index.tsx, hocs/roleRoute.tsx) -- `landingRouteFor` in
 * backend/sagas/onboardingRedirect.ts was built and unit-tested but never
 * wired into any of them, so a volunteer-only user (holds manage_volunteering,
 * not read_households) fell all the way through to the final `return
 * "/households"` below and landed in an empty ContactsPage he never asked
 * for, instead of /volunteering.
 *
 * Fix: delegate to `landingRouteFor` FIRST (after the pre-existing !user /
 * !isApproved gates, which it doesn't itself know about and which must still
 * win, e.g. an unapproved volunteer-only user still sees /unAuthPage, not
 * /volunteering). `landingRouteFor` answers exactly two special cases --
 * unfinished-onboarding wins over everything, then volunteer-only ->
 * /volunteering -- and falls back to its own DASHBOARD_ROOT ("/dashboard")
 * when neither applies. That fallback is deliberately NOT returned directly:
 * doing so would override the read_routes -> "/saved-routes" and default ->
 * "/households" branches below for every ordinary account holder who isn't
 * in DASHBOARD_ROLES, which is a real regression this function must not
 * introduce. So DASHBOARD_ROOT specifically means "neither special case
 * applies -- fall through to the ordinary chain below, unchanged."
 */
export function getDefaultLandingPage(user: IUser | null): string {
  if (!user) return "/login";

  if (!user.isApproved) return "/unAuthPage";

  const composed = landingRouteFor(user);
  if (composed !== DASHBOARD_ROOT) {
    return composed;
  }

  const workspaceRole = getWorkspaceRole(user);

  // Admin / Anash / Super Admin land on the dashboard.
  if (workspaceRole && DASHBOARD_ROLES.includes(workspaceRole)) {
    return "/dashboard";
  }

  if (canAccessAny(user, "read_routes", "routes")) {
    return "/saved-routes";
  }

  return "/households";
}